import { useMemo } from "react"

import { Gauge } from "lucide-react"

import type { StatChip } from "@/components/shared/StatBand"
import {
  useCollectionsConfig,
  useLocations,
  useRoutesPlanned,
  useVehicleTypes,
} from "@/hooks/useCollections"
import { routeCapacity, routeLoadFactor, toCapacityStop } from "@/lib/loadFactor"
import { findVehicleType } from "@/lib/routeCostCalculation"
import { calculateRouteStats } from "@/lib/statusDisplay"
import { loadFactorTone } from "@/lib/tone"
import type { Route } from "@/types/collections"

export function useRouteLoadFactors(routes: Route[]): Map<string, number> {
  const { data: locationsData } = useLocations()
  const { data: vehicleTypesData } = useVehicleTypes()
  const { data: configData } = useCollectionsConfig()
  const { data: routesPlannedData } = useRoutesPlanned()

  const plannedRouteVehicleTypes = useMemo(() => {
    const map = new Map<string, string>()
    if (routesPlannedData?.data) {
      routesPlannedData.data.forEach((rp) => {
        if (rp.vehicle_type) {
          map.set(rp.id, rp.vehicle_type)
        }
      })
    }
    return map
  }, [routesPlannedData])

  return useMemo(() => {
    const map = new Map<string, number>()
    if (!vehicleTypesData?.data || !locationsData?.data) return map

    routes.forEach((route) => {
      const vehicleTypeName =
        route.vehicleType ||
        (route.plannedRouteId ? plannedRouteVehicleTypes.get(route.plannedRouteId) : undefined)
      let vehicleType = vehicleTypeName
        ? findVehicleType(vehicleTypesData.data, vehicleTypeName)
        : null
      if (!vehicleType && route.isAdhoc && vehicleTypesData.data.length > 0) {
        vehicleType = vehicleTypesData.data[0]
      }
      if (!vehicleType) return

      // preferActuals: this board reports what happened, so a served stop counts its actuals.
      const loadFactor = routeLoadFactor({
        vehicleType,
        stops: route.stops.map((stop) => toCapacityStop(stop, { preferActuals: true })),
        locations: locationsData.data,
        config: configData?.data,
      })
      if (loadFactor !== null) map.set(route.id, loadFactor)
    })
    return map
  }, [routes, vehicleTypesData, locationsData, configData, plannedRouteVehicleTypes])
}

export function useDayStats(visibleRoutes: Route[]) {
  const { data: locationsData } = useLocations()
  const { data: vehicleTypesData } = useVehicleTypes()
  const { data: configData } = useCollectionsConfig()

  const stats = useMemo(() => {
    const baseStats = calculateRouteStats(visibleRoutes)

    let totalUsedCapacity = 0
    let totalCapacity = 0
    let hasActuals = false

    let totalUkPalletCapacity = 0
    let totalUkPalletUsed = 0

    if (vehicleTypesData?.data && locationsData?.data && visibleRoutes.length > 0) {
      visibleRoutes.forEach((route) => {
        const vehicleTypeName = route.vehicleType
        let vehicleType = vehicleTypeName
          ? findVehicleType(vehicleTypesData.data, vehicleTypeName)
          : null
        // Ad-hoc routes often have no planned_route; fall back to the first vehicle type so the %
        // matches the Data tab.
        if (!vehicleType && route.isAdhoc && vehicleTypesData.data.length > 0) {
          vehicleType = vehicleTypesData.data[0]
        }
        if (!vehicleType) return

        const routeHasActuals = route.stops.some(
          (s) =>
            s.loadingUnitsActual !== undefined &&
            s.loadingUnitsActual !== null &&
            s.loadingUnitsActual > 0
        )
        if (routeHasActuals) hasActuals = true

        const hasShipperCollection = route.stops.some(
          (stop) => stop.slotType === "shipper_collection"
        )

        if (!hasShipperCollection) return

        const capacity = routeCapacity({
          vehicleType,
          stops: route.stops.map((stop) => toCapacityStop(stop, { preferActuals: true })),
          locations: locationsData.data,
          config: configData?.data,
        })
        if (capacity) {
          totalUsedCapacity += capacity.totalFillPercentage
          totalCapacity += 1
          if (capacity.ukPalletCapacity > 0) {
            totalUkPalletCapacity += capacity.ukPalletCapacity
            totalUkPalletUsed += capacity.ukPalletsUsed
          }
        }
      })
    }

    const fillPercentage =
      totalUkPalletCapacity > 0
        ? (totalUkPalletUsed / totalUkPalletCapacity) * 100
        : totalCapacity > 0
          ? totalUsedCapacity / totalCapacity
          : 0

    const overallCapacity =
      totalCapacity > 0
        ? {
            totalUsed: 0,
            totalCapacity: totalCapacity,
            totalRemaining: 0,
            fillPercentage: fillPercentage,
            basedOnActuals: hasActuals,
            ukPalletEquivalent:
              totalUkPalletCapacity > 0
                ? {
                    totalCapacity: totalUkPalletCapacity,
                    totalUsed: totalUkPalletUsed,
                  }
                : undefined,
          }
        : undefined

    return {
      ...baseStats,
      overallVehicleCapacity: overallCapacity,
    }
  }, [visibleRoutes, vehicleTypesData, locationsData, configData])

  const statChips = useMemo(() => {
    const chips: StatChip[] = []

    const capacity = stats.overallVehicleCapacity
    if (capacity) {
      const pallets = capacity.ukPalletEquivalent
      chips.push({
        key: "load_factor",
        icon: <Gauge size={12} aria-hidden="true" />,
        label: `${capacity.fillPercentage.toFixed(0)}% load`,
        // A visible hint rather than a title, so it reads on touch and to keyboards.
        hint: pallets
          ? `${pallets.totalUsed.toFixed(1)}/${pallets.totalCapacity.toFixed(0)} pallets`
          : undefined,
        tone: loadFactorTone(capacity.fillPercentage),
      })
    }

    return chips
  }, [stats])

  return { stats, statChips }
}
